/**
 * Alert presentation (pure): burst coalescing, toast lifetimes and wording.
 *
 * Alerts are production heads-ups ("the aircraft is 2 min from the camera site"), never
 * flight-safety instructions — the UI labels severities as INFO / ADVISORY / PRIORITY, not
 * "warning", and never adds imperative advice to them.
 */
import type { Alert, AlertRule, AlertSeverity, Id } from '@apos/shared';

export const SEVERITY_RANK: Record<AlertSeverity, number> = { info: 0, advisory: 1, warning: 2 };
export const SEVERITY_LABEL: Record<AlertSeverity, string> = { info: 'INFO', advisory: 'ADVISORY', warning: 'PRIORITY' };

/** Auto-dismiss after (ms); 0 = stays until acknowledged. */
export function toastLifetimeMs(severity: AlertSeverity): number {
  return severity === 'warning' ? 0 : severity === 'advisory' ? 10_000 : 6_000;
}

/** Alerts arriving within this window are shown together. */
export const BURST_WINDOW_MS = 1200;

export interface AlertGroup {
  key: string;
  severity: AlertSeverity;
  /** newest first */
  alerts: Alert[];
  title: string;
  body: string;
  t: number;
}

type RuleKind = AlertRule['spec']['type'] | 'other';

/** Group nouns for coalesced titles; rule types without one read "N ALERTS". */
const KIND_NOUN: Partial<Record<RuleKind, string>> = {
  proximity: 'PROXIMITY',
  eta: 'ARRIVAL',
  cpa: 'APPROACH',
  sun: 'LIGHT',
  weather_change: 'WEATHER',
  stale_data: 'DATA',
  separation: 'SEPARATION',
  phase_change: 'PHASE',
} as Partial<Record<RuleKind, string>>;
const KNOWN_KINDS = new Set<string>(['proximity', 'eta', 'cpa', 'sun', 'weather_change', 'stale_data', 'separation', 'phase_change', 'runsheet_due', 'crew_off_position']);

function ruleKind(a: Alert, rules: ReadonlyMap<Id, AlertRule>): RuleKind {
  const r = a.ruleId ? rules.get(a.ruleId) : undefined;
  if (r) return r.spec.type;
  const k = a.dedupeKey.split('|')[1]?.split(':')[0];
  return k && KNOWN_KINDS.has(k) ? (k as RuleKind) : 'other';
}

/** "HERON H01 — 0.1 NM FROM TOVE" → ["HERON H01", "0.1 NM FROM TOVE"]. */
export function splitTitle(title: string): [string, string] {
  const i = title.indexOf(' — ');
  return i > 0 ? [title.slice(0, i), title.slice(i + 3)] : ['', title];
}

/**
 * Group a burst: same rule kind + same lead subject (usually the aircraft) collapse into one
 * entry — four "T01 — 0.1 NM FROM x" alerts become "HERON H01 — 4 PROXIMITY ALERTS".
 * Groups come back most severe first, then newest.
 */
export function coalesceAlerts(alerts: readonly Alert[], rules: readonly AlertRule[] = []): AlertGroup[] {
  const ruleMap = new Map(rules.map((r) => [r.id, r]));
  const groups = new Map<string, Alert[]>();
  for (const a of alerts) {
    const kind = ruleKind(a, ruleMap);
    const lead = a.subjects.find((s) => s.kind === 'aircraft') ?? a.subjects[0];
    const [head] = splitTitle(a.title);
    const key = `${kind}|${lead ? `${lead.kind}:${lead.id}` : head || a.dedupeKey}`;
    const g = groups.get(key);
    if (g) g.push(a);
    else groups.set(key, [a]);
  }
  const out: AlertGroup[] = [];
  for (const [key, list] of groups) {
    const sorted = [...list].sort((x, y) => y.t - x.t);
    const severity = sorted.reduce<AlertSeverity>((s, a) => (SEVERITY_RANK[a.severity] > SEVERITY_RANK[s] ? a.severity : s), 'info');
    const first = sorted[0]!;
    if (sorted.length === 1) {
      out.push({ key, severity, alerts: sorted, title: first.title, body: first.body, t: first.t });
      continue;
    }
    const kind = key.split('|')[0] as RuleKind;
    const [head] = splitTitle(first.title);
    const noun = KIND_NOUN[kind] ? `${KIND_NOUN[kind]} ALERTS` : 'ALERTS';
    const title = head ? `${head} — ${sorted.length} ${noun}` : `${sorted.length} ${noun}`;
    const tails = sorted.map((a) => splitTitle(a.title)[1]);
    const shown = tails.slice(0, 4).join(' · ');
    out.push({ key, severity, alerts: sorted, title, body: tails.length > 4 ? `${shown} · +${tails.length - 4} more` : shown, t: first.t });
  }
  out.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.t - a.t);
  return out;
}

/** Field UX spec §7.5: alert text must never imply separation or clearance. */
export const PROHIBITED_ALERT_WORDS = /\b(clear|cleared|safe|no traffic)\b/i;
export const hasProhibitedWording = (text: string) => PROHIBITED_ALERT_WORDS.test(text);

export function isAcked(a: Pick<Alert, 'ackedBy'>, userId: Id | null | undefined): boolean {
  return !!userId && a.ackedBy.includes(userId);
}

/** Unacknowledged advisory+ alerts for this user. */
export function unackedCount(alerts: readonly Alert[], userId: Id | null | undefined): number {
  let n = 0;
  for (const a of alerts) if (a.severity !== 'info' && !isAcked(a, userId)) n++;
  return n;
}
